import React, {memo} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {MobileHeaderMenu} from 'components/MobileHeader/MobileHeaderMenu'
import {MobileTopAppBanner} from 'components/MobileTopAppBanner'
import {MyListHeaderEntry} from 'components/MyListHeaderEntry'
import {SearchFormMobile} from 'components/SearchBox/Home/SearchFormMobile'
import {SearchSummaryButtonWithStore} from 'components/SearchSummaryButtonWithStore'
import {Web2AppHoldOutWrapper} from 'components/Web2AppHoldOutWrapper'
import {useIsPageSearchBoxOpen} from 'hooks/searchBox'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {getUserCountryCode} from 'modules/meta/selectors'
import {OverlayType} from 'modules/overlay/types'
import {SearchBoxAncestor} from 'modules/searchBox/types'

import {Box} from '@vio/atlas/helpers/Box'
import {useIsReferralProgramEnabled} from '@vio/atlas/hooks/useIsReferralProgramEnabled'
import {NavBar, StartContent} from '@vio/atlas/NavBar'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {Entity, Team} from '@vio/core/src/analytics/types/Events'
import {getUser, selectAuthEnabled} from '@vio/core/src/auth/modules/selectors'
import {isFlutterAppWebView} from '@vio/core/src/native'
import {NavBarBrandLogo} from '@vio/shared/src/navBar'
import {MyListLinkComponent} from '@vio/shared/src/priceWatch/hooks/useTrackMyListLinkClick'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo/BrandLogo'

const BottomContentWrapper = styled.div`
  padding-top: ${cssTheme.layout.spacing.s300};
`

const HeaderWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;

  ${StartContent} {
    margin-left: 0;
  }
`

interface Props {
  hasDivider?: boolean
  hasHeaderSearchSummary?: boolean
}

const BaseHomeMobileHeaderComponent = ({
  hasDivider = false,
  hasHeaderSearchSummary = false
}: Props) => {
  const showOverlay = useShowOverlay()
  const {brand} = useBrand()
  const user = useSelector(getUser)
  const authEnabled = useSelector(selectAuthEnabled)
  const userCountryCode = useSelector(getUserCountryCode)
  const isInviteFriendsTab = useIsReferralProgramEnabled(brand)
  const isPageSearchBoxOpen = useIsPageSearchBoxOpen()
  const {
    shell,
    retention: {hasWeb2App}
  } = useBrandCapabilities()
  const isWebView = isFlutterAppWebView()

  const handleShowPreferences = () => {
    showOverlay({
      type: OverlayType.Preferences,
      entity: Entity.PreferencesOverlay,
      team: Team.Retention,
      payload: {
        isInviteFriendsTab
      }
    })
  }

  // The search box always lives in the centerContent: a compact summary button
  // when collapsed and the full search form (dropped to its own full-width row
  // via wrapCenterContent) when expanded. It never relocates to another slot.
  const showSearchForm = hasHeaderSearchSummary && isPageSearchBoxOpen
  const showSearchSummary = hasHeaderSearchSummary && !isPageSearchBoxOpen

  const centerContent = showSearchForm ? (
    <BottomContentWrapper>
      <SearchFormMobile />
    </BottomContentWrapper>
  ) : showSearchSummary ? (
    <SearchSummaryButtonWithStore ancestor={SearchBoxAncestor.Page} />
  ) : undefined

  const logo = (
    <NavBarBrandLogo
      brand={brand}
      compact={showSearchSummary}
      withIconText={shell.logoHasIconText}
      BrandLogoComponent={BrandLogo}
    />
  )

  return (
    <>
      <Web2AppHoldOutWrapper
        element={<MobileTopAppBanner />}
        enabled={!isWebView && hasWeb2App && userCountryCode !== 'US'}
        holdOutElement={null}
      />
      <HeaderWrapper>
        <NavBar
          variant="transparent"
          withDivider={hasDivider}
          wrapCenterContent={showSearchForm}
          startContent={logo}
          centerContent={centerContent}
          endContent={
            <Box display="flex" alignItems="center">
              <MyListHeaderEntry
                component={MyListLinkComponent.HomeMobileHeader}
                iconOnly
              />
              <MobileHeaderMenu
                shouldShowUserAvatar={authEnabled}
                user={user}
                onShowPreferences={handleShowPreferences}
              />
            </Box>
          }
        />
      </HeaderWrapper>
    </>
  )
}

export const BaseHomeMobileHeader = memo(BaseHomeMobileHeaderComponent)
